/**
 * Single source of truth for the `ui.linkTo.role` codes that drive
 * cross-sheet leaf inheritance.
 *
 * WHY THIS FILE EXISTS — these two literals were previously re-declared in
 * three places that must agree:
 *   - `role8-statement-of-financial-position.validator.ts` (headless SFP)
 *   - `role12-statement-of-profit-or-loss.validator.ts`    (headless P&L)
 *   - `cross-sheet-recompute.util.ts`                       (mounted parity)
 * If the editor's `control.ui.linkTo.role` code ever changes, all three must
 * change together. Hoisting both constants into this tiny, dependency-free
 * module lets every consumer import the same value instead of carrying its own
 * copy — and keeps the recompute util free of a heavyweight import from the
 * two large validator modules (which would risk an import cycle as those
 * modules grow).
 *
 * All three codes mirror the editor's leaf-inheritance filters:
 *   - SFP pulls Role-9 sub-classification leaves where
 *     `control.ui.linkTo.role === '210100'` (pullRole9Values()).
 *   - P&L pulls Role-13 analysis-of-profit-or-loss leaves where
 *     `control.ui.linkTo.role === '310100'` (pullRole13Values()).
 *   - SOCI pulls Role-12's `Profit (loss)` where
 *     `control.ui.linkTo.role === '310000'` (pullRole12Values()).
 */

/** Role-9 (sub-classification of assets/liabilities/equity) link code used by SOFP (Role 8). */
export const ROLE9_SUBCLASSIFICATION_LINK_ROLE = '210100';

/** Role-13 (analysis of profit or loss) link code used by SOPL (Role 12). */
export const ROLE13_ANALYSIS_LINK_ROLE = '310100';

/**
 * Role-12 (statement of profit or loss) link code used by SOCI (Role 15).
 *
 * NESTED INHERITANCE, unlike the two above — Role 12 is itself an inheriting
 * sheet (it pulls Role-13 leaves), so anything recomputing Role 12 as an
 * inheritance SOURCE must resolve Role 13 into it first or it reads a stale
 * `Profit (loss)`. `buildRole12ProfitOrLossSnapshot` in
 * `role12-statement-of-profit-or-loss.rules.ts` is the single recipe for that;
 * `cross-sheet-recompute.util.ts` resolves the same chain recursively.
 */
export const ROLE12_PROFIT_OR_LOSS_LINK_ROLE = '310000';
